import { useState, useEffect, useCallback, useMemo } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { ASSESSMENT_GROUP_KEYS, ASSESSMENT_GROUPS, assessmentTemplateId } from '../../Helpers/assessmentGroups';
import { REQUIRED_DEFAULT_TYPES } from '../../Helpers/requiredDefaultTypes';

type ConfigPriority = 'required' | 'recommended' | 'optional';

interface SubFieldStatus {
    key: string;
    label: string;
    configured: boolean;
}

export interface ConfigItemStatus {
    key: string;
    path: string;
    label: string;
    description: string;
    priority: ConfigPriority;
    configured: boolean;
    loading: boolean;
    settingsLink?: string;
    subFields?: SubFieldStatus[];
}

interface ConfigItemDefinition {
    key: string;
    path: string;
    label: string;
    description: string;
    priority: ConfigPriority;
    settingsLink?: string;
    subFields?: { key: string; label: string }[];
    /**
     * Item spread over several fixed paths: configured when every path exists.
     * `path` is then the first of them and `subFields` keys are the other paths' last segments.
     */
    paths?: string[];
}

const CONFIG_ITEMS: ConfigItemDefinition[] = [
    {
        key: 'buildComponentTypes',
        path: 'config/buildComponentTypes',
        label: 'Komponenttyper & underhållsmallar',
        description: 'Definierar vilka komponenttyper som finns tillgängliga i systemet',
        priority: 'required',
    },
    {
        key: 'documentCategories',
        path: 'config/documentCategories',
        label: 'Dokumentkategorier',
        description: 'Kategorier för dokumenthantering',
        priority: 'required',
        settingsLink: '/settings/categories/document',
    },
    {
        key: 'materials',
        path: 'config/materials',
        label: 'Material',
        description: 'Materialdefinitioner för byggnadskomponenter',
        priority: 'required',
    },
    {
        key: 'locationCategories',
        path: 'locationCategories',
        label: 'Platskategorier',
        description: 'Kategorihierarki för platser i byggnader',
        priority: 'required',
        settingsLink: '/settings/categories/location',
    },
    {
        key: 'enabledBuildingTypes',
        path: 'global/enabledBuildingTypes',
        label: 'Byggnadstyper',
        description: 'Vilka byggnadstyper instansen erbjuder — utan dem kan inga byggnader skapas (superadmin)',
        priority: 'required',
        settingsLink: '/superadmin/instance-settings',
    },
    {
        key: 'instanceName',
        path: 'global/instanceName',
        label: 'Instansnamn',
        description: 'Kundnamn för instansen',
        priority: 'required',
    },
    {
        key: 'customerType',
        path: 'global/customerType',
        label: 'Kundtyp',
        description: 'Stiftelse, bostadsrättsförening eller fastighetsägare — styr vilka fält som visas (superadmin)',
        priority: 'required',
        settingsLink: '/superadmin/instance-settings',
    },
    {
        key: 'loginPage',
        path: 'publicSettings/loginPage',
        label: 'Inloggningssida',
        description: 'Varumärkesanpassning av inloggningssidan',
        priority: 'recommended',
        settingsLink: '/settings/general',
        subFields: [
            { key: 'backgroundImageUrl', label: 'Bakgrundsbild' },
            { key: 'customerLogoUrl', label: 'Kundlogotyp' },
            { key: 'smallLogoUrl', label: 'Liten logotyp' },
        ],
    },
    {
        key: 'maintenanceTemplates',
        path: 'config/maintenanceTemplates',
        label: 'Underhållsmallar',
        description: 'Underhållsmallar kopplade till komponenttyper',
        priority: 'recommended',
        settingsLink: '/maintenanceTemplates',
    },
    {
        key: 'conditionAssessmentTemplates',
        path: `inspections/templates/${assessmentTemplateId('roofAndDrainage')}`,
        paths: ASSESSMENT_GROUP_KEYS.map(group => `inspections/templates/${assessmentTemplateId(group)}`),
        label: 'Skickbedömningsmallar',
        description: 'Tre systemmallar för skickbedömning (tak, fasad, ytskikt) som schemalägger bedömningarna som kontroller',
        priority: 'recommended',
        settingsLink: '/settings/component-types?tab=assessment',
        subFields: ASSESSMENT_GROUP_KEYS.map(group => ({
            key: assessmentTemplateId(group),
            label: ASSESSMENT_GROUPS[group].label,
        })),
    },
    {
        key: 'requiredDefaultTypes',
        path: `config/buildComponentTypes/${REQUIRED_DEFAULT_TYPES[0].key}`,
        paths: REQUIRED_DEFAULT_TYPES.map(entry => `config/buildComponentTypes/${entry.key}`),
        label: 'Standardtyper för appen',
        description: 'Komponenttyper som iOS-appen skapar komponenter med (t.ex. lås). Livslängd och kostnad sätts i Komponenttyper',
        priority: 'recommended',
        settingsLink: '/settings/component-types',
        subFields: REQUIRED_DEFAULT_TYPES.map(entry => ({ key: entry.key, label: entry.name })),
    },
    {
        key: 'buildingTags',
        path: 'buildingTags',
        label: 'Byggnadstaggar',
        description: 'Taggdefinitioner för byggnader',
        priority: 'optional',
    },
];

/** Required paths, shared with SetupWarningBanner so the two lists cannot drift. */
export const REQUIRED_CONFIG_PATHS = CONFIG_ITEMS
    .filter(item => item.priority === 'required')
    .map(item => item.path);

/** The three Skickbedömning template paths, shared with SetupWarningBanner (admin-level warning). */
export const ASSESSMENT_TEMPLATE_PATHS: string[] =
    CONFIG_ITEMS.find(item => item.key === 'conditionAssessmentTemplates')?.paths ?? [];

interface UseInstanceSetupReturn {
    items: ConfigItemStatus[];
    loading: boolean;
    requiredComplete: boolean;
    requiredItems: ConfigItemStatus[];
    recommendedItems: ConfigItemStatus[];
    optionalItems: ConfigItemStatus[];
    refresh: () => void;
}

export const useInstanceSetup = (): UseInstanceSetupReturn => {
    const [items, setItems] = useState<ConfigItemStatus[]>(
        CONFIG_ITEMS.map(item => ({
            ...item,
            configured: false,
            loading: true,
            subFields: item.subFields?.map(sf => ({ ...sf, configured: false })),
        }))
    );
    const [loading, setLoading] = useState(true);

    const checkConfigs = useCallback(async () => {
        const auth = getAuth();
        if (!auth.currentUser) return;

        const db = getDatabase();

        setItems(prev => prev.map(item => ({ ...item, loading: true })));
        setLoading(true);

        const results = await Promise.allSettled(
            CONFIG_ITEMS.map(async (item) => {
                if (item.paths) {
                    // Multi-path item: every path must exist; sub-fields report each path
                    const pathResults = await Promise.allSettled(item.paths.map(path => get(ref(db, path))));
                    const pathExists = pathResults.map(result => result.status === 'fulfilled' && result.value.exists());
                    return {
                        ...item,
                        configured: pathExists.every(Boolean),
                        loading: false,
                        subFields: item.subFields?.map((sf, i) => ({ ...sf, configured: pathExists[i] ?? false })),
                    };
                }

                const snapshot = await get(ref(db, item.path));
                const exists = snapshot.exists();

                let subFields: SubFieldStatus[] | undefined;
                if (item.subFields && exists) {
                    const subResults = await Promise.allSettled(
                        item.subFields.map(async (sf) => {
                            const sfSnapshot = await get(ref(db, `${item.path}/${sf.key}`));
                            return {
                                ...sf,
                                configured: sfSnapshot.exists() && !!sfSnapshot.val(),
                            };
                        })
                    );
                    subFields = subResults.map((result, i) =>
                        result.status === 'fulfilled'
                            ? result.value
                            : { ...item.subFields![i], configured: false }
                    );
                } else if (item.subFields) {
                    subFields = item.subFields.map(sf => ({ ...sf, configured: false }));
                }

                return {
                    ...item,
                    configured: exists,
                    loading: false,
                    subFields,
                };
            })
        );

        const updatedItems: ConfigItemStatus[] = results.map((result, i) =>
            result.status === 'fulfilled'
                ? result.value
                : {
                    ...CONFIG_ITEMS[i],
                    configured: false,
                    loading: false,
                    subFields: CONFIG_ITEMS[i].subFields?.map(sf => ({ ...sf, configured: false })),
                }
        );

        setItems(updatedItems);
        setLoading(false);
    }, []);

    useEffect(() => {
        checkConfigs();
    }, [checkConfigs]);

    const requiredItems = useMemo(() => items.filter(i => i.priority === 'required'), [items]);
    const recommendedItems = useMemo(() => items.filter(i => i.priority === 'recommended'), [items]);
    const optionalItems = useMemo(() => items.filter(i => i.priority === 'optional'), [items]);
    const requiredComplete = useMemo(() => requiredItems.every(i => i.configured), [requiredItems]);

    return {
        items,
        loading,
        requiredComplete,
        requiredItems,
        recommendedItems,
        optionalItems,
        refresh: checkConfigs,
    };
};
